import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import {
    useAppDispatch,
    useAppSelector,
    ticketInfoSelector,
    setTicketInfo,
    setSentMessage,
    setEditingMessage,
    useSendMessageMutation,
    setTicketStatus,
    sentMessageSelector,
    setTicketList,
    ticketListSelector,
    useUserProfileQuery,
} from '../../../app/store';

import {
    TicketMessageType,
    TicketFileType,
    UserInfoType,
} from '../ticket.types';
import { immediateToast } from 'utils/toast';

const useTicketMessageHandlers = () => {
    const { ticketId } = useParams();
    const dispatch = useAppDispatch();
    const { data: profileInfo } = useUserProfileQuery();
    const ticketInfo = useAppSelector(ticketInfoSelector);
    const sentMessage = useAppSelector(sentMessageSelector);
    const ticketList = useAppSelector(ticketListSelector);

    const [
        sendMessage,
        { isLoading: loadingSendMessage, isError: errorSendMessage },
    ] = useSendMessageMutation();

    const handleSendMessage = useCallback(
        async (
            currentData: string,
            setCurrentMessage: React.Dispatch<React.SetStateAction<string>>,
            attachmentData: TicketFileType,
            setAttachmentData: React.Dispatch<
                React.SetStateAction<TicketFileType>
            >,
            oldMessage?: TicketMessageType | undefined,
            parentId?: number | null,
        ) => {
            if (currentData?.length > 2 || attachmentData?.length > 0) {
                const now = new Date();
                const timestamp = now.toISOString();

                const text = currentData;
                const files = attachmentData.map((item) =>
                    typeof item?.file === 'string' ? item?.file : '',
                );

                let finalFiles: any[] = [];

                if (oldMessage) {
                    finalFiles = [...files];
                    finalFiles = finalFiles.map((item) => {
                        const firstSlashIndex = item?.lastIndexOf('/');
                        if (firstSlashIndex >= 0) {
                            return item.substring(firstSlashIndex + 1);
                        } else {
                            return item;
                        }
                    });
                }

                setCurrentMessage('');
                setAttachmentData([]);

                const replyTarget = parentId
                    ? ticketInfo?.message?.items?.find(
                          (message) => message.id === parentId,
                      )
                    : undefined;

                let newMessage: TicketMessageType = {
                    id: oldMessage?.id || 0,
                    created_at: oldMessage?.created_at || timestamp,
                    updated_at: oldMessage ? '' : timestamp,
                    status: 'active',
                    user: {
                        id: profileInfo?.id ?? '',
                        name: profileInfo?.name ?? '',
                        family: profileInfo?.family ?? '',
                        avatar: profileInfo?.avatar ?? '',
                        isAdmin: false,
                    } as UserInfoType,
                    text: text,
                    files: oldMessage ? finalFiles : files,
                    isNotSent: true,
                    parent_id: parentId ?? oldMessage?.parent_id ?? null,
                    reply_to: replyTarget
                        ? {
                              id: replyTarget.id,
                              text: replyTarget.text,
                              user: replyTarget.user,
                          }
                        : oldMessage?.reply_to ?? null,
                };

                dispatch(setSentMessage(newMessage));
                handleSendMessageToServer(newMessage);

                if (!oldMessage) {
                    if (ticketInfo?.message) {
                        dispatch(
                            setTicketInfo({
                                ...ticketInfo,
                                message: {
                                    ...ticketInfo.message,
                                    items: [
                                        newMessage,
                                        ...ticketInfo.message.items,
                                    ],
                                },
                            }),
                        );
                    }
                } else {
                    if (ticketInfo?.message) {
                        dispatch(
                            setTicketInfo({
                                ...ticketInfo,
                                message: {
                                    ...ticketInfo.message,
                                    items: ticketInfo.message.items.map(
                                        (message) => {
                                            if (message.id !== newMessage.id)
                                                return message;

                                            const linksFromOldMessage =
                                                oldMessage?.files?.map(
                                                    (obj) => obj?.file,
                                                ) ?? [];
                                            const validLinksFromOldMessage =
                                                linksFromOldMessage.filter(
                                                    (link) =>
                                                        link !== undefined,
                                                );
                                            const combinedArray = [
                                                ...validLinksFromOldMessage,
                                                ...files,
                                            ];
                                            const uniqueCombinedArray =
                                                Array.from(
                                                    new Set(combinedArray),
                                                );

                                            return {
                                                ...newMessage,
                                                files: uniqueCombinedArray,
                                            };
                                        },
                                    ),
                                },
                            }),
                        );
                    }
                }
            } else {
                immediateToast('error', {
                    message:
                        'برای ارسال پیام جدید باید حداقل یک فایل یا یک متن با بیشتر از ۳ کارکتر وارد کنید.',
                    timeout: 3000,
                });
            }
        },
        [profileInfo, ticketInfo],
    );

    const handleSendMessageToServer = useCallback(
        async (currentMessage: TicketMessageType) => {
            const currentData = currentMessage ? currentMessage : sentMessage;

            if (currentData) {
                const isNewMessage = currentData?.id === 0;

                try {
                    const requestTextOptions = {
                        id: Number(ticketId),
                        message: {
                            text: currentData?.text,
                            files: currentData?.files,
                            parent_id: currentData?.parent_id ?? null,
                        },
                    };

                    const response = await sendMessage(requestTextOptions);

                    if ('data' in response) {
                        if (response?.data?.text) {
                            const newTicketList = ticketList?.map((ticket) =>
                                ticket.id === Number(ticketId)
                                    ? {
                                          ...ticket,
                                          last_message: {
                                              ...ticket.last_message,
                                              text: response.data.text,
                                          },
                                      }
                                    : ticket,
                            );

                            dispatch(setTicketList(newTicketList));
                        }

                        const serverMessage = response?.data;

                        if (!isNewMessage) {
                            if (ticketInfo?.message) {
                                dispatch(
                                    setTicketInfo({
                                        ...ticketInfo,
                                        status: 'answered',
                                        message: {
                                            ...ticketInfo.message,
                                            items: ticketInfo.message.items.map(
                                                (message) =>
                                                    message.id ===
                                                    serverMessage?.id
                                                        ? serverMessage
                                                        : message,
                                            ),
                                        },
                                    }),
                                );
                            }
                        } else {
                            if (ticketInfo?.message) {
                                const updatedMessageItems = [
                                    ...ticketInfo.message?.items,
                                ];

                                if (updatedMessageItems[0]?.id === 0) {
                                    updatedMessageItems[0] = serverMessage;
                                } else {
                                    updatedMessageItems.unshift(serverMessage);
                                }

                                dispatch(
                                    setTicketInfo({
                                        ...ticketInfo,
                                        status: 'answered',
                                        message: {
                                            ...ticketInfo.message,
                                            items: updatedMessageItems,
                                        },
                                    }),
                                );
                            }
                        }

                        dispatch(setSentMessage(undefined));
                        dispatch(setEditingMessage(undefined));
                    }
                } catch (error) {
                    console.error('error', error);
                }
            }
        },
        [ticketId, sendMessage, ticketInfo],
    );

    return {
        onSendMessage: handleSendMessage,
        onSendMessageToServer: handleSendMessageToServer,
        loadingSendMessage,
        errorSendMessage,
    };
};

export default useTicketMessageHandlers;
